import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useRecoilState, useRecoilValue } from 'recoil';
import { modalVisibleState } from 'recoils/common/commonState';
import {
  basicUserInfoState,
  bizUserInfoState,
} from 'recoils/user/userInfoState';
import {
  dataLocalState,
  userSNSState,
  userTokenState,
  whatIsUserRoleState,
} from 'recoils/user/userState';

import { deleteAccountUser, getAccountUserDeletable } from 'apis/accountApi';

import useToast from 'hooks/components/useToast';

import { TokenManager, dataLocalManager } from 'utils/security/securityUtil';

import { dataLocalKeyProviderConst } from 'constants/common/providerConst';
import { bizUserInfoConst } from 'constants/console/userInfo/bizUserInfoConst';
import { userModalStateConst } from 'constants/console/userInfo/userInfoConst';

/**
 * @function useUserInfoWithdrawal
 * @desc 회원 탈퇴를 담당하는 커스텀 훅
 * @returns {Object} 회원 탈퇴 관련 함수와 상태
 */
const useUserInfoWithdrawal = () => {
  const toast = useToast();
  // Recoil 상태
  const [userToken, setUserToken] = useRecoilState(userTokenState);
  const [, setVisibleModal] = useRecoilState(modalVisibleState);
  const [basicUserInfo] = useRecoilState(basicUserInfoState);
  const [, setBizUserInfo] = useRecoilState(bizUserInfoState);
  const [, setSnsUserInfo] = useRecoilState(userSNSState);
  const dataLocal = useRecoilValue(dataLocalState);

  // 로컬 상태
  const [checkedRadio, setCheckedRadio] = useState('');
  const [withdrawalCheck, setWithdrawalCheck] = useState(false);

  // 공통 Hooks
  const navigate = useNavigate();

  /**
   * @function fetchDataCurrentSns
   * @desc 현재 SNS 데이터를 가져오는 비동기 함수
   * @returns {Promise<void>}
   */
  const fetchDataCurrentSns = async () => {
    const { sns } = dataLocalKeyProviderConst;
    if (dataLocal) {
      try {
        const data = await dataLocalManager.getInfo({
          snsValue: dataLocal[sns.key],
        });
        if (data) {
          const { sns } = data;
          setSnsUserInfo((prev) => ({ ...prev, sns: sns }));
        }
      } catch (err) {}
    }
  };

  /**
   * @function resetLogout
   * @desc 로그아웃 시 호출되는 함수
   * @returns {void}
   */
  const resetLogout = (path) => {
    fetchDataCurrentSns();
    !path && navigate('/');
    setSnsUserInfo({
      isopen: false,
      role: '',
      sns: '',
      email: '',
      nickname: '',
      url: '/',
    });
    setUserToken(null);
    TokenManager.clearAccessToken(); // localStorage에서 삭제
  };

  /**
   * @function onChangeBizFinalClick
   * @desc 사업자 회원으로 전환하기 모달에서 [전환] 버튼 클릭 시 동작하는 함수
   * @returns {void}
   */
  const onChangeBizFinalClick = () => {
    // 1) '사업자 정보' 영역 표출
    // 1-1) 회원 정보 데이터 계산
    const newData = bizUserInfoConst.map((el) => {
      if (el.name === 'contactName') {
        return {
          ...el,
          content: basicUserInfo.filter((item) => item.name === 'name')[0]
            .content,
        };
      } else if (el.name === 'contactPhone') {
        return {
          ...el,
          content: basicUserInfo.filter((item) => item.name === 'phone')[0]
            .content,
        };
      } else {
        return el;
      }
    });
    setBizUserInfo(newData);
    navigate('/user-biz-change');

    // 2) 모달 닫힘
    setVisibleModal((prev) => ({
      ...prev,
      isopen: false,
    }));
  };

  /**
   * @function bizChangeRadio
   * @desc 체크박스의 값 변경 시 호출되는 핸들러
   * @param {Event} e - 이벤트 객체
   * @returns {void}
   */
  const bizChangeRadio = (e) => {
    setCheckedRadio(e.target.value);
  };

  /**
   * @function onWithDrawalCheck
   * @desc 회원 탈퇴 체크박스 클릭
   * @param {Event} e - 이벤트 객체
   * @returns {void}
   */
  const onWithDrawalCheck = (e) => {
    setWithdrawalCheck(e.target.checked);
  };

  /**
   * @function withDrawalData
   * @desc 회원 탈퇴 여부 API 호출 후 조건 처리
   * 탈퇴 여부 API 호출 👻
   * @param {string} token - 회원 토큰
   * @returns {void}
   */
  const withDrawalData = async (token) => {
    const reasonConst = {
      '삭제하지 않은 APP이 존재합니다.': userModalStateConst.withDrawal.app,
      'APP에 청구된 요금이 존재합니다.': userModalStateConst.withDrawal.biling,
    };
    try {
      const { success, data } = await getAccountUserDeletable();
      if (success) {
        const { reason, deletable } = data;
        if (!deletable) {
          // 탈퇴가 불가능한 경우
          return setVisibleModal((prev) => ({
            ...prev,
            isopen: true,
            modalType: reasonConst[reason],
          }));
        } else {
          /**
           * 탈퇴할 조건이 된 회원의 경우 모달 표시
           * → [탈퇴] 버튼 클릭 시 회원 탈퇴 및 로그아웃이 진행됨
           * 에러 코드 0
           */
          return setVisibleModal((prev) => ({
            ...prev,
            isopen: true,
            modalType: userModalStateConst.withDrawal.complete,
          }));
        }
      }
    } catch (error) {
      // 에러를 다시 던져 ErrorBoundary로 전달
      throw error;
    }
  };

  /**
   * @function onUserWithDrawalClick
   * @desc 회원 탈퇴 여부가 끝난 후 모달에서 [탈퇴] 버튼 클릭 함수
   * @returns {void}
   */
  const onUserWithDrawalClick = async () => {
    // 1) 모달 닫힘
    setVisibleModal((prev) => ({
      ...prev,
      isopen: false,
    }));
    try {
      // 2) 탈퇴 API 호출
      const { success } = await deleteAccountUser();
      if (success) {
        toast.show('success', '회원 탈퇴 및 로그아웃 되었습니다.');
      }
    } catch (error) {
      // 에러를 다시 던져 ErrorBoundary로 전달
      throw error;
    } finally {
      // 성공 여부와 상관없이 실행되는 초기화 작업
      resetLogout();
    }
  };

  /**
   * @function onBizWithDrawalClick
   * @desc 사업자 회원 탈퇴(사업자 계정 이관) 모달에서 [탈퇴] 버튼 클릭 함수
   * @returns {void}
   */
  const onBizWithDrawalClick = () => {
    switch (checkedRadio) {
      case '':
      case '계정 이관 신청':
        // 1) 모달 닫힘
        setVisibleModal((prev) => ({
          ...prev,
          isopen: false,
        }));
        // 2) 1:1 문의에 계정 이관 문의로 넘어감
        return navigate('/partnership-inquiry?type=accountTransfer');
      case '계정 이관 없이 바로 탈퇴':
        // 탈퇴 API 호출
        return withDrawalData(userToken);
      default:
        return;
    }
  };

  /**
   * @function onWithDrawalBtnClick
   * @desc [탈퇴] 버튼 클릭 시 모달 표출
   * @param {string} whatIsRole 사용자 역할 (개인 또는 사업자)
   * @returns {void}
   */
  const onWithDrawalBtnClick = (whatIsRole) => {
    switch (whatIsRole) {
      // 개인
      case 'USER':
        // 탈퇴 여부 API 호출 👻
        return withDrawalData(userToken);
      // 사업자
      case 'BIZ':
        return setVisibleModal({
          isopen: true,
          modalName: '회원 탈퇴하기',
          modalType: userModalStateConst.bizWithdrawal,
        });
      default:
        return;
    }
  };

  return {
    withdrawalCheck,
    onWithDrawalCheck,
    onChangeBizFinalClick,
    onUserWithDrawalClick,
    checkedRadio,
    bizChangeRadio,
    onBizWithDrawalClick,
    onWithDrawalBtnClick,
    resetLogout,
  };
};

export default useUserInfoWithdrawal;
